import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import {
  whatsappTemplateService,
  whatsappAutomationService,
  type WhatsappTemplate,
  type WhatsappAutomationRule,
} from '../services';
import { useToast } from '../hooks/useToast';

interface TemplateFormState {
  name: string;
  body: string;
  active: boolean;
}

const WhatsappModulePage: React.FC = () => {
  const { showSuccess, showError, showInfo } = useToast();

  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsappTemplate | null>(null);
  const [formState, setFormState] = useState<TemplateFormState>({
    name: '',
    body: '',
    active: true,
  });

  const [rules, setRules] = useState<WhatsappAutomationRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSavingId, setRulesSavingId] = useState<string | null>(null);

  const statusOptions: { value: string; label: string }[] = [
    { value: 'RECEBIDO', label: 'Recebido' },
    { value: 'EM_PREPARO', label: 'Em preparo' },
    { value: 'PRONTO', label: 'Pronto' },
    { value: 'EM_ROTA', label: 'Em rota' },
    { value: 'ENTREGUE', label: 'Entregue' },
    { value: 'CANCELADO', label: 'Cancelado' },
  ];

  const hasLoadedRef = useRef(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await whatsappTemplateService.listTemplates();
      setTemplates(data);
    } catch (err: any) {
      const message = err.message || 'Erro ao carregar templates de WhatsApp';
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadRules = useCallback(async () => {
    try {
      setRulesLoading(true);
      const data = await whatsappAutomationService.listRules();
      setRules(data);
    } catch (err: any) {
      const message = err.message || 'Erro ao carregar regras de automação de WhatsApp';
      showError(message);
    } finally {
      setRulesLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;

    void (async () => {
      await loadTemplates();
      await loadRules();
    })();
  }, [loadTemplates, loadRules]);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormState({ name: '', body: '', active: true });
    setDialogOpen(true);
  };

  const handleOpenEdit = (tpl: WhatsappTemplate) => {
    setEditingTemplate(tpl);
    setFormState({ name: tpl.name, body: tpl.body, active: tpl.active });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formState.name.trim() || !formState.body.trim()) {
      showInfo('Preencha nome e corpo do template.');
      return;
    }

    try {
      setSaving(true);

      if (editingTemplate) {
        const updated = await whatsappTemplateService.updateTemplate(editingTemplate.id, {
          name: formState.name.trim(),
          body: formState.body.trim(),
          active: formState.active,
        });

        setTemplates((old) => old.map((t) => (t.id === updated.id ? updated : t)));
        showSuccess('Template atualizado com sucesso.');
      } else {
        const created = await whatsappTemplateService.createTemplate({
          name: formState.name.trim(),
          body: formState.body.trim(),
          active: formState.active,
        });

        setTemplates((old) => [...old, created]);
        showSuccess('Template criado com sucesso.');
      }

      setDialogOpen(false);
      setEditingTemplate(null);
    } catch (err: any) {
      const message = err.message || 'Erro ao salvar template de WhatsApp';
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            WhatsApp
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gerencie os templates de mensagens de WhatsApp usados nas regras de automação de
            status de pedidos.
          </Typography>
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          WhatsApp
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gerencie os templates de mensagens de WhatsApp que serão usados nas regras de automação
          por status de pedido.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6">Templates de WhatsApp</Typography>
            <Typography variant="body2" color="text.secondary">
              Crie modelos reutilizáveis de mensagem e depois vincule-os às regras de automação
              por status.
            </Typography>
          </Box>

          <Button variant="contained" onClick={handleOpenCreate} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
            Novo template
          </Button>
        </Box>

        {templates.length === 0 ? (
          <Alert severity="info">Nenhum template cadastrado ainda. Clique em "Novo template" para criar.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Ativo</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Criado em</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tpl) => (
                <TableRow key={tpl.id} hover>
                  <TableCell>{tpl.name}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip
                      label={tpl.active ? 'Ativo' : 'Inativo'}
                      color={tpl.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {new Date(tpl.createdAt).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEdit(tpl)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Regras de automação por status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Defina qual template de WhatsApp será disparado automaticamente para cada status de
          pedido. Apenas regras ativas serão consideradas.
        </Typography>

        <Paper elevation={1} sx={{ p: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status do pedido</TableCell>
                <TableCell>Template associado</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statusOptions.map((status) => {
                const existingRule = rules.find(
                  (r) => r.event === 'STATUS_ALTERADO' && r.statusFilter === status.value
                );

                const selectedTemplateId = existingRule?.templateId || '';
                const isActive = existingRule ? existingRule.active : false;

                const handleChangeTemplate = (value: string) => {
                  setRules((old) => {
                    const rule = old.find(
                      (r) => r.event === 'STATUS_ALTERADO' && r.statusFilter === status.value
                    );
                    if (!rule) {
                      const now = new Date().toISOString();
                      return [
                        ...old,
                        {
                          id: `local-${status.value}`,
                          cnpj: '',
                          event: 'STATUS_ALTERADO',
                          statusFilter: status.value,
                          active: true,
                          templateId: value || undefined,
                          templateBody: undefined,
                          createdAt: now,
                          updatedAt: now,
                        },
                      ];
                    }
                    return old.map((r) =>
                      r === rule
                        ? {
                            ...r,
                            templateId: value || undefined,
                          }
                        : r
                    );
                  });
                };

                const handleChangeActive = (checked: boolean) => {
                  setRules((old) => {
                    const rule = old.find(
                      (r) => r.event === 'STATUS_ALTERADO' && r.statusFilter === status.value
                    );
                    if (!rule) {
                      const now = new Date().toISOString();
                      return [
                        ...old,
                        {
                          id: `local-${status.value}`,
                          cnpj: '',
                          event: 'STATUS_ALTERADO',
                          statusFilter: status.value,
                          active: checked,
                          templateId: undefined,
                          templateBody: undefined,
                          createdAt: now,
                          updatedAt: now,
                        },
                      ];
                    }
                    return old.map((r) => (r === rule ? { ...r, active: checked } : r));
                  });
                };

                const handleSave = async () => {
                  const latestRule = rules.find(
                    (r) => r.event === 'STATUS_ALTERADO' && r.statusFilter === status.value
                  );

                  if (!latestRule || !latestRule.templateId) {
                    showInfo('Selecione um template antes de salvar esta regra.');
                    return;
                  }

                  try {
                    setRulesSavingId(status.value);

                    if (latestRule.id.startsWith('local-')) {
                      const created = await whatsappAutomationService.createRule({
                        statusFilter: status.value,
                        templateId: latestRule.templateId,
                        active: latestRule.active,
                      });

                      setRules((old) => [
                        ...old.filter(
                          (r) => !(r.id.startsWith('local-') && r.statusFilter === status.value)
                        ),
                        created,
                      ]);
                      showSuccess('Regra de automação criada com sucesso.');
                    } else {
                      const updated = await whatsappAutomationService.updateRule(latestRule.id, {
                        statusFilter: status.value,
                        templateId: latestRule.templateId,
                        active: latestRule.active,
                      });

                      setRules((old) => old.map((r) => (r.id === updated.id ? updated : r)));
                      showSuccess('Regra de automação atualizada com sucesso.');
                    }
                  } catch (err: any) {
                    const message = err.message || 'Erro ao salvar regra de automação de WhatsApp';
                    showError(message);
                  } finally {
                    setRulesSavingId(null);
                  }
                };

                return (
                  <TableRow key={status.value} hover>
                    <TableCell>{status.label}</TableCell>
                    <TableCell>
                      <Select
                        value={selectedTemplateId}
                        onChange={(e) => handleChangeTemplate(e.target.value as string)}
                        size="small"
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>Sem template definido</em>
                        </MenuItem>
                        {templates.map((tpl) => (
                          <MenuItem key={tpl.id} value={tpl.id}>
                            {tpl.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={isActive}
                        onChange={(e) => handleChangeActive(e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSave}
                        disabled={rulesLoading || rulesSavingId === status.value}
                      >
                        {rulesSavingId === status.value ? 'Salvando...' : 'Salvar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTemplate ? 'Editar template de WhatsApp' : 'Novo template de WhatsApp'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              label="Nome do template"
              fullWidth
              margin="normal"
              value={formState.name}
              onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
              disabled={saving}
              required
            />

            <TextField
              label="Corpo da mensagem"
              fullWidth
              margin="normal"
              value={formState.body}
              onChange={(e) => setFormState((prev) => ({ ...prev, body: e.target.value }))}
              disabled={saving}
              required
              multiline
              minRows={4}
              helperText="Use variáveis como {{pedidoId}}, {{cnpj}} e {{status}} conforme configurado no backend."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formState.active}
                  onChange={(e) => setFormState((prev) => ({ ...prev, active: e.target.checked }))}
                  disabled={saving}
                />
              }
              label="Template ativo"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default WhatsappModulePage;
